import { getColumnsForActiveTab } from '@/components/organisms/ApplyDataGrid/table/grid-columns';
import { ApplicationState } from '@/store';
import { getAgencies } from '@/store/agency/actions';
import { getApplyConfigRecord } from '@/store/apply_config/actions';
import { getConfigRecord } from '@/store/config/actions';
import { setCustomTabs, requestFilters, switchTab } from '@/store/offers-dashboard/actions';
import { getIntakeId, getRole, getUniId } from '@/store/selectors';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { assignActiveTab, generateTabs } from './helpers';
import { QueryKeys } from '@/types';
import _ from 'lodash';
import { getActiveStageTab } from '@/utilities/helpers';
import { setIsLoading } from '@/store/ui/actions';
import { isPartnerRole } from '@/utilities/roles/permissions';

/**
 * Version of useGetActiveTab that is integrated into offers dashboard state.
 */
export const useLoadActiveTab = (defaultTab: string, queryKey: string) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const headerTab = useSelector(({ ui }: ApplicationState) => ui.headerTab);
  useEffect(() => {
    const activeTab = getActiveStageTab(searchParams, headerTab, defaultTab, queryKey);
    dispatch(switchTab(activeTab));
  }, [headerTab, searchParams]);
};

/**
 * Custom hook to load uni config and apply config whenever the uni changes.
 * @param uni
 */
export const useLoadUniConfig = (uni: string) => {
  const dispatch = useDispatch();
  const userRole = useSelector(getRole);
  useEffect(() => {
    dispatch(getConfigRecord(uni));
    if (!isPartnerRole(userRole)) {
      dispatch(getApplyConfigRecord(uni));
    }
  }, [uni, userRole]);
};

/**
 * Custom hook that generates custom tabs from the apply dashboard tab configuration.
 * @param activeTab
 */
export const useLoadTabs = activeTab => {
  const dispatch = useDispatch();
  const { totals, totalsLoaded, userRole, customTabs, customTabsLoaded } = useSelector((state: ApplicationState) => ({
    totals: state.offersDashboard.stageTotals,
    totalsLoaded: state.offersDashboard.allTotalsLoaded,
    userRole: state.auth.role,
    customTabs: state.offersDashboard.customTabs,
    customTabsLoaded: state.offersDashboard.tabsLoaded,
  }));
  useEffect(() => {
    const { customTabs: newCustomTabs } = generateTabs(activeTab, totals, userRole);
    dispatch(setCustomTabs(newCustomTabs));
  }, [totalsLoaded, totals]);

  useEffect(() => {
    if (customTabsLoaded) {
      const { customTabs: newCustomTabs } = assignActiveTab(customTabs, activeTab);
      dispatch(setCustomTabs(newCustomTabs));
    }
  }, [activeTab]);
};

export const useLoading = () => {
  const { isLoaded } = useSelector(({ offersDashboard }: ApplicationState) => ({
    isLoaded:
      offersDashboard.dashboardRowsLoaded && offersDashboard.allTotalsLoaded && offersDashboard.filterOptionsLoaded,
  }));
  const dispatch = useDispatch();
  useEffect(() => {
    if (!isLoaded) dispatch(setIsLoading(true));
    else dispatch(setIsLoading(false));
  }, [isLoaded]);
};

/**
 * Custom hook that loads agents. Refreshes on initial load.
 */
export const useLoadAgents = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getAgencies());
  }, []);
};

/**
 * Custom hook that loads filters. Refreshes on new activeTab, intake, and university changes.
 * @param activeTab
 */
export const useLoadFilters = activeTab => {
  const dispatch = useDispatch();
  const authIntake = useSelector(getIntakeId);
  const authUni = useSelector(getUniId);
  useEffect(() => {
    dispatch(requestFilters());
  }, [activeTab, authIntake, authUni, dispatch]);
};

/**
 * Custom hook that removes non-viable sort keys.  Refreshes whenever searchparams
 * change.
 */
export const useUpdateSearchParams = activeTab => {
  const userRole = useSelector(getRole);
  const [searchParams, setSearchParams] = useSearchParams();
  const viableSortKeys = useMemo(
    () => getColumnsForActiveTab({ userRole, activeTab }, 0).map(c => c.key),
    [userRole, activeTab],
  );
  useEffect(() => {
    const sortCol = searchParams.get[QueryKeys.SORT_COL];
    if (sortCol && !viableSortKeys.includes(sortCol)) {
      // remove from params
      searchParams.delete(QueryKeys.SORT_COL);
      searchParams.delete(QueryKeys.SORT_COL);
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, viableSortKeys]);
};
